import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const isWeb = Platform.OS === 'web';

export const isVoiceSupported = () => {
  if (!isWeb || typeof window === 'undefined') return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * Continuous voice-command listener using the Web Speech API.
 *
 * Recognised phrases:
 *   "turn page" / "next page" / "next" / "go forward"   → onNextPage
 *   "go back" / "previous page" / "previous" / "prev"   → onPrevPage
 *   "play" / "start" / "begin"                          → onPlay
 *   "stop" / "pause" / "halt"                           → onStop
 *
 * Auto-restarts after silence so you don't have to tap again.
 * Stops cleanly when `enabled` becomes false or on unmount.
 */
export const useVoiceCommands = ({ enabled, onNextPage, onPrevPage, onPlay, onStop }) => {
  // Keep latest callbacks in refs so the recognition handler never goes stale
  const callbacksRef = useRef({ onNextPage, onPrevPage, onPlay, onStop });
  useEffect(() => {
    callbacksRef.current = { onNextPage, onPrevPage, onPlay, onStop };
  });

  const recognitionRef = useRef(null);
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  useEffect(() => {
    if (!isWeb || !enabled) {
      stopRecognition();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      if (!last.isFinal) return;
      const phrase = last[0].transcript.trim().toLowerCase();
      const { onNextPage: next, onPrevPage: prev, onPlay: play, onStop: stop } = callbacksRef.current;

      if (/\b(turn page|next page|next|go forward|forward page)\b/.test(phrase)) {
        next?.();
      } else if (/\b(go back|previous page|previous|prev|back page)\b/.test(phrase)) {
        prev?.();
      } else if (/\b(play|start playing|begin)\b/.test(phrase)) {
        play?.();
      } else if (/\b(stop|pause|halt)\b/.test(phrase)) {
        stop?.();
      }
    };

    recognition.onerror = (e) => {
      // 'not-allowed' means mic permission was denied — stop trying
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        stopRecognition();
      }
    };

    // Auto-restart after silence so listening stays active
    recognition.onend = () => {
      if (enabledRef.current && recognitionRef.current === recognition) {
        try { recognition.start(); } catch (_) {}
      }
    };

    try {
      recognition.start();
    } catch (e) {
      recognitionRef.current = null;
    }

    return stopRecognition;
  }, [enabled]); // only re-run when enabled changes

  function stopRecognition() {
    const r = recognitionRef.current;
    if (r) {
      recognitionRef.current = null;
      r.onend = null;
      r.onresult = null;
      try { r.stop(); } catch (_) {}
    }
  }
};

export default useVoiceCommands;
